/**
 * Constants for the application shell.
 *
 * Nav items, section ids and both polling intervals live here rather than as
 * literals inside the components, so the nav order is a data change and the
 * intervals are visible in one place.
 *
 * Audit references prevented here: App.jsx #15, shared.jsx #10, shared.jsx #14.
 */

/** How often the header clock advances, in milliseconds. */
export const CLOCK_TICK_INTERVAL_MS = 1000;

/**
 * How often the shell checks whether the Eastern calendar date has changed, in
 * milliseconds. Thirty seconds bounds the worst-case staleness of the header
 * date to half a minute after midnight without polling every second.
 */
export const DAY_ROLLOVER_CHECK_INTERVAL_MS = 30_000;

/**
 * Temporary avatar until the profile feature owns per-user photos.
 *
 * TR's own photo, served from this app's public folder. It is a single named
 * constant with one reference, so replacing it with the profile API is a one-line
 * change. The previous build pasted the filename inline in two components, which
 * meant every person on the account saw the same face with no way to tell.
 */
export const INTERIM_AVATAR_URL = '/profile-photo.png';

/**
 * Sections of the dashboard, in render order.
 *
 * `id` is both the DOM anchor the quick nav scrolls to and the key the feature
 * slice will mount into. `presentation` records how the old app surfaced it:
 * `panel` sections sit in the page, `overlay` sections open over it. The shell
 * anchors all of them for now and the overlay behaviour returns with each slice.
 */
export const APP_SECTIONS = Object.freeze([
  Object.freeze({ id: 'section-master-calendar', title: 'Master Calendar', presentation: 'overlay' }),
  // Today's master-calendar day, read only, ahead of Calories so the day's plan is the
  // first thing on the dashboard rather than something behind the calendar overlay.
  Object.freeze({ id: 'section-master-day', title: 'Today at a glance', presentation: 'panel' }),
  Object.freeze({ id: 'section-calories', title: 'Calories', presentation: 'panel' }),
  Object.freeze({ id: 'section-training', title: 'Training', presentation: 'panel' }),
  Object.freeze({ id: 'section-food', title: 'Food log', presentation: 'overlay' }),
  Object.freeze({ id: 'section-hydration', title: 'Hydration', presentation: 'overlay' }),
  Object.freeze({ id: 'section-health', title: 'Health', presentation: 'panel' }),
  Object.freeze({ id: 'section-nearby', title: 'Nearby', presentation: 'panel' }),
]);

/**
 * Quick nav pills, in order.
 *
 * Master Calendar is first because it is the only entry that opens a view of
 * everything at once, so it reads as the way in rather than one section of many.
 *
 * Icons are vector, not emoji. An emoji renders as a materially different picture
 * on Apple, Google and Microsoft platforms, so the previous build's nav looked
 * like a different product depending on the device.
 */
export const QUICK_NAV_ITEMS = Object.freeze([
  Object.freeze({ sectionId: 'section-master-calendar', emoji: '🗂️', label: 'Master Calendar' }),
  Object.freeze({ sectionId: 'section-calories', emoji: '🔥', label: 'Calories' }),
  Object.freeze({ sectionId: 'section-training', emoji: '💪', label: 'Training' }),
  Object.freeze({ sectionId: 'section-food', emoji: '🍽️', label: 'Food log' }),
  Object.freeze({ sectionId: 'section-hydration', emoji: '💧', label: 'Hydration' }),
  Object.freeze({ sectionId: 'section-health', emoji: '❤️', label: 'Health' }),
  Object.freeze({ sectionId: 'section-nearby', emoji: '🗺️', label: 'Nearby' }),
]);

/** Pixel size of a quick nav icon. */
export const QUICK_NAV_ICON_SIZE = 14;

/** Standing footnote under the dashboard. */
export const HANDOFF_FOOTNOTE =
  'Reminders and searches surface and hand off, they never place an order. ' +
  'Checkout always happens in the app you open.';

/**
 * The app's own version and release notes, shown on Account's About tab.
 *
 * One source of truth for the number itself, and a short history behind it — bumped by
 * hand alongside a real release, not generated from commits, so every line is something a
 * user would actually recognise as a change rather than internal noise.
 */
export const APP_VERSION = '1.0.2';
// Newest first: `AccountPanel`'s "Last updated" line reads `APP_CHANGELOG[0].date`.
export const APP_CHANGELOG = Object.freeze([
  Object.freeze({
    version: '1.0.2',
    date: '2026-08-10',
    notes: Object.freeze([
      'Nearby now recommends specific restaurants with order links, photos, and sort by rating or distance.',
      'Signed-in devices can be given a nickname, and show their approximate location and last-active time.',
    ]),
  }),
  Object.freeze({
    version: '1.0.1',
    date: '2026-08-09',
    notes: Object.freeze([
      'Grocery items can be ordered online or bought in store, each with its own links or details.',
      'Vitamins can carry product links, and vitamin reminders arrive per time of day instead of all at once.',
      'FatSecret sync no longer announces a food update unless something actually changed.',
      'The connection screen says when it is retrying, instead of an unchanging "loading" message.',
      'Settings redesigned to match Goals, with icon-coloured fields.',
      "Master Day's three cards refresh automatically after a change anywhere in the app.",
    ]),
  }),
  Object.freeze({
    version: '1.0.0',
    date: '2026-08-08',
    notes: Object.freeze([
      'Calorie and macro tracking, with rest-day targets and a fasting window.',
      'A full training program with warm-ups, logged sets and a weekly comparison.',
      'Health tracking: vitamins by time of day, blood work, weigh-ins and appointments.',
      'A grocery list with online-order links and in-store details.',
      'A master calendar tying training, meals and health together by day.',
      'Smart reminders for water, meals, training and vitamins, by app and SMS.',
      'Apple Watch, FatSecret and Google Calendar integration.',
    ]),
  }),
]);
